import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Container, Typography, Box, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RefreshIcon from '@mui/icons-material/Refresh';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Reset error state when location changes
    if (prevProps.location !== this.props.location && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              textAlign: 'center',
              gap: 3,
              py: 4
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 2,
                width: '100%',
                background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: 80,
                  color: 'error.main',
                  mb: 2,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                    '50%': {
                      transform: 'scale(1.1)',
                      opacity: 0.7,
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              />
              <Typography
                variant="h4"
                color="error"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  background: 'linear-gradient(45deg, #d32f2f 30%, #f44336 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Oops! Something went wrong
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{
                  mb: 3,
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                }}
              >
                {this.state.error?.message || 'An unexpected error occurred. Please try again or contact support if the problem persists.'}
              </Typography>
              <ErrorFallback onNavigate={() => this.setState({ hasError: false, error: null })} />
            </Paper>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

const ErrorBoundaryWrapper = (props) => {
  const location = useLocation();
  return <ErrorBoundary {...props} location={location} />;
};

const ErrorFallback = ({ onNavigate }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleNavigation = (path) => {
    onNavigate(); 
    navigate(path);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}
    >
      {token ? (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleNavigation('/dashboard')}
          startIcon={<DashboardIcon />}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Go to Dashboard
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleNavigation('/')}
          startIcon={<HomeIcon />}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Go to Home
        </Button>
      )}
      <Button
        variant="outlined"
        color="primary"
        onClick={handleRefresh}
        startIcon={<RefreshIcon />}
        sx={{
          px: 3,
          py: 1,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '1rem',
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        Refresh Page
      </Button>
    </Box>
  );
};

export default ErrorBoundaryWrapper; 